"use client"

import type React from "react"

import { useCallback, useEffect, useMemo, useState } from "react"
import { Briefcase, Building, Code, DollarSign, Filter, Globe, GraduationCap, MapPin, Settings, Star, Type, Zap } from "lucide-react"
import { useForm, useWatch } from "react-hook-form"

import { type ResolvedLocationSelection } from "@/components/location-autocomplete-input"
import { useToast } from "@/hooks/use-toast"
import {
  buildFilterFormDataFromDraft,
  extractSearchFiltersDraft,
  normalizeStructuredLocation,
  type FilterFormData,
  type FilterLocationFullItem,
  type SearchFiltersDraft,
} from "@/lib/search-filter-model"
import { usePostSearchFiltersEnrich } from "openapi/api/endpoints"

import { CompanyFiltersSection } from "./search/filter-dialog/company-filters-section"
import { EditFiltersDialogShell } from "./search/filter-dialog/edit-filters-dialog-shell"
import { type EditFiltersSectionDescriptor, type EditFiltersSectionId } from "./search/filter-dialog/filter-dialog-types"
import { GeneralFiltersSection } from "./search/filter-dialog/general-filters-section"
import { JobFiltersSection } from "./search/filter-dialog/job-filters-section"
import { LocationFiltersSection } from "./search/filter-dialog/location-filters-section"
import { SkillsFiltersSection } from "./search/filter-dialog/skills-filters-section"

interface EditFiltersDialogProps {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  initialFilters?: Partial<SearchFiltersDraft>
  onSubmit?: (filters: SearchFiltersDraft) => Promise<void> | void
  searchPrompt?: string
  children?: React.ReactNode
  title?: string
  description?: string
  submitLabel?: string
}

/**
 * Primary editor for the parsed search draft. The dialog keeps one stable external
 * contract, while the shell and section rendering live in smaller components so
 * new filter areas can be added without expanding one monolithic file again.
 */
export function EditFiltersDialog({
  open,
  onOpenChange,
  initialFilters,
  onSubmit,
  searchPrompt,
  children,
  title = "Edit your search filters",
  description = "Review what will run in the search and adjust the filters before continuing.",
  submitLabel = "Apply filters",
}: EditFiltersDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false)
  const [activeSection, setActiveSection] = useState<EditFiltersSectionId>("general")
  const [searchFilters, setSearchFilters] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { toast } = useToast()
  const isControlled = open !== undefined
  const isOpen = open ?? internalOpen
  const initialFormValues = useMemo(() => buildFilterFormDataFromDraft(initialFilters), [initialFilters])

  const updateOpenState = useCallback(
    (nextOpen: boolean) => {
      if (!isControlled) {
        setInternalOpen(nextOpen)
      }
      onOpenChange?.(nextOpen)
    },
    [isControlled, onOpenChange],
  )

  const { control, handleSubmit, setValue, getValues, reset } = useForm<FilterFormData>({
    defaultValues: initialFormValues,
    shouldUnregister: false,
  })
  const watchedFormValues = useWatch({ control }) as FilterFormData | undefined
  const formValues = watchedFormValues ?? initialFormValues

  const { mutateAsync: getAdditionalJobValues, isPending: isFindingSimilarJobTitles } = usePostSearchFiltersEnrich()
  const { mutateAsync: getAdditionalSkillValues, isPending: isFindingSimilarSkills } = usePostSearchFiltersEnrich()
  const { mutateAsync: getAdditionalKeywordValues, isPending: isFindingSimilarKeywords } = usePostSearchFiltersEnrich()

  useEffect(() => {
    if (isOpen) {
      reset(initialFormValues)
    }
  }, [initialFormValues, isOpen, reset])

  const filterSections = useMemo<EditFiltersSectionDescriptor[]>(
    () => [
      {
        id: "general",
        label: "General",
        icon: Settings,
        active: Boolean(formValues.minExperience || formValues.maxExperience),
      },
      {
        id: "locations",
        label: "Locations",
        icon: MapPin,
        active: Boolean(formValues.locations.length || formValues.locationRegion.length || formValues.locationCountry),
      },
      {
        id: "job",
        label: "Job",
        icon: Briefcase,
        active: Boolean(formValues.jobTitles.length || formValues.timeSpentMin.length || formValues.jobTitleLevels.length),
      },
      {
        id: "company",
        label: "Company",
        icon: Building,
        active: Boolean(
          formValues.companies.length ||
            formValues.excludedCompanies.length ||
            formValues.companyIndustries.length ||
            formValues.companySizes.length,
        ),
      },
      {
        id: "skills",
        label: "Skills or Keywords",
        icon: Code,
        active: Boolean(formValues.skills.length || formValues.keywords.length),
      },
      { id: "funding", label: "Funding & Revenue", icon: DollarSign, active: false },
      { id: "power", label: "Power Filters", icon: Zap, active: false },
      { id: "likely", label: "Likely to Switch", icon: Star, active: false },
      { id: "education", label: "Education", icon: GraduationCap, active: false },
      { id: "languages", label: "Languages", icon: Globe, active: false },
      { id: "boolean", label: "Boolean & Name", icon: Type, active: false },
    ],
    [formValues],
  )

  const visibleSections = useMemo(() => {
    const search = searchFilters.trim().toLowerCase()
    return filterSections.filter((section) => {
      if (formValues.hideInactiveFilters && !section.active) return false
      if (!search) return true
      return section.label.toLowerCase().includes(search)
    })
  }, [filterSections, formValues.hideInactiveFilters, searchFilters])

  useEffect(() => {
    const isCurrentSectionVisible = visibleSections.some((section) => section.id === activeSection)
    if (!isCurrentSectionVisible) {
      setActiveSection(visibleSections[0]?.id ?? "general")
    }
  }, [activeSection, visibleSections])

  const removeFromListField = (
    field: "jobTitles" | "skills" | "keywords" | "locations" | "locationRegion" | "companies" | "excludedCompanies",
    value: string,
  ) => {
    const current = getValues(field)
    setValue(
      field,
      current.filter((item) => item !== value),
      { shouldDirty: true },
    )
  }

  const addFromInputToListField = (
    inputField: "jobTitleInput" | "skillInput" | "keywordsInput" | "locationInput",
    listField: "jobTitles" | "skills" | "keywords" | "locations",
  ) => {
    const input = getValues(inputField).trim()
    const current = getValues(listField)
    if (!input || current.includes(input)) return

    setValue(listField, [...current, input], { shouldDirty: true })
    setValue(inputField, "", { shouldDirty: true })
  }

  const addCommaSeparatedToListField = (
    inputField: "companyInput" | "excludedCompanyInput",
    listField: "companies" | "excludedCompanies",
  ) => {
    const input = getValues(inputField).trim()
    const current = getValues(listField)
    if (!input) return

    const additions = input
      .split(",")
      .map((item) => item.trim())
      .filter((item) => item && !current.includes(item))

    if (additions.length === 0) return

    setValue(listField, [...current, ...additions], { shouldDirty: true })
    setValue(inputField, "", { shouldDirty: true })
  }

  const removeJobTitle = (title: string) => removeFromListField("jobTitles", title)
  const addJobTitle = () => addFromInputToListField("jobTitleInput", "jobTitles")

  const mergeUniqueStrings = (existing: string[], incoming: string[]) => {
    const seen = new Set(existing.map((value) => value.trim()).filter(Boolean))
    const merged = [...existing]

    incoming.forEach((value) => {
      const normalizedValue = value.trim()
      if (!normalizedValue || seen.has(normalizedValue)) return
      seen.add(normalizedValue)
      merged.push(normalizedValue)
    })

    return merged
  }

  const handleFindSimilarJobTitles = async () => {
    try {
      const currentTitles = getValues("jobTitles")
      if (currentTitles.length === 0) return

      const similarTitles = await getAdditionalJobValues({ data: { existingOps: currentTitles, type: "jobs" } })
      if (similarTitles.length > 0) {
        setValue("jobTitles", mergeUniqueStrings(currentTitles, similarTitles), { shouldDirty: true })
      }
    } catch {
      toast({
        title: "An error occured",
        description: "Failed to fetch additinal jobs",
        color: "red",
      })
    }
  }

  const removeSkill = (skill: string) => removeFromListField("skills", skill)
  const removeKeyword = (keyword: string) => removeFromListField("keywords", keyword)
  const addSkill = () => addFromInputToListField("skillInput", "skills")
  const addKeyword = () => addFromInputToListField("keywordsInput", "keywords")

  const handleFindSimilarSkills = async () => {
    try {
      const currentSkills = getValues("skills")
      if (currentSkills.length === 0) return

      const similarSkills = await getAdditionalSkillValues({ data: { existingOps: currentSkills, type: "skills" } })
      if (similarSkills.length > 0) {
        setValue("skills", [...currentSkills, ...similarSkills], { shouldDirty: true })
      }
    } catch {
      toast({
        title: "An error occured",
        description: "Failed to fetch additinal skills",
        color: "red",
      })
    }
  }

  const handleFindSimilarKeywords = async () => {
    try {
      const currentKeywords = getValues("keywords")
      const currentRoles = getValues("jobTitles")
      const currentSkills = getValues("skills")

      if (currentKeywords.length === 0 || currentRoles.length === 0) return

      const similarKeywords = await getAdditionalKeywordValues({
        data: {
          existingOps: currentKeywords,
          type: "keywords",
          roles: currentRoles,
          ...(currentSkills.length > 0 ? { skills: currentSkills } : {}),
          ...(searchPrompt?.trim() ? { prompt: searchPrompt.trim() } : {}),
        },
      })

      if (similarKeywords.length > 0) {
        setValue("keywords", mergeUniqueStrings(currentKeywords, similarKeywords), { shouldDirty: true })
      }
    } catch {
      toast({
        title: "An error occured",
        description: "Failed to fetch additinal keywords",
        color: "red",
      })
    }
  }

  const removeLocation = (location: string) => {
    removeFromListField("locations", location)
    const nextLocationFull = getValues("locationFull").filter((item) => item.city !== location)
    setValue("locationFull", nextLocationFull, { shouldDirty: true })
  }

  const removeRegion = (region: string) => {
    removeFromListField("locationRegion", region)
    const nextLocationFull = getValues("locationFull")
      .map((item) => {
        if (item.region !== region) return item
        if (!item.city) return null

        const nextLabel = item.city ?? item.label ?? ""
        return normalizeStructuredLocation({
          ...item,
          region: null,
          label: nextLabel,
        })
      })
      .filter((item): item is FilterLocationFullItem => item !== null)

    setValue("locationFull", nextLocationFull, { shouldDirty: true })
  }

  const removeStructuredLocation = (locationToRemove: FilterLocationFullItem) => {
    const nextLocationFull = getValues("locationFull").filter((item) => {
      const normalizedItem = normalizeStructuredLocation(item)
      const normalizedTarget = normalizeStructuredLocation(locationToRemove)

      return !(
        normalizedItem.city === normalizedTarget.city &&
        normalizedItem.region === normalizedTarget.region &&
        normalizedItem.country === normalizedTarget.country &&
        normalizedItem.label === normalizedTarget.label
      )
    })

    const nextCities = Array.from(new Set(nextLocationFull.map((item) => item.city).filter(Boolean) as string[]))
    const nextRegions = Array.from(new Set(nextLocationFull.map((item) => item.region).filter(Boolean) as string[]))
    const nextCountry = nextLocationFull.find((item) => item.country)?.country ?? ""

    setValue("locationFull", nextLocationFull, { shouldDirty: true })
    setValue("locations", nextCities, { shouldDirty: true })
    setValue("locationRegion", nextRegions, { shouldDirty: true })
    setValue("locationCountry", nextCountry, { shouldDirty: true })
  }

  const selectLocation = (location: ResolvedLocationSelection) => {
    const city =
      location.city?.trim() || (!location.region && !location.countryCode ? location.label.trim() : "")
    const region = location.region?.trim()
    const countryCode = location.countryCode ?? undefined
    const normalizedLocation = normalizeStructuredLocation({
      city: city || null,
      region: region || null,
      country: countryCode ?? null,
      label: location.normalizedLabel ?? location.label,
    })

    if (city) {
      const currentLocations = getValues("locations")
      if (!currentLocations.includes(city)) {
        setValue("locations", [...currentLocations, city], { shouldDirty: true })
      }
    }

    if (region) {
      const currentRegions = getValues("locationRegion")
      if (!currentRegions.includes(region)) {
        setValue("locationRegion", [...currentRegions, region], { shouldDirty: true })
      }
    }

    if (countryCode) {
      setValue("locationCountry", countryCode, { shouldDirty: true })
    }

    setValue("locationInput", "", { shouldDirty: true })

    const currentLocations = getValues("locationFull")
    const existingIndex = currentLocations.findIndex(
      (item) =>
        item.city === normalizedLocation.city &&
        item.region === normalizedLocation.region &&
        item.country === normalizedLocation.country &&
        item.label === normalizedLocation.label,
    )

    if (existingIndex === -1) {
      setValue("locationFull", [...currentLocations, normalizedLocation], { shouldDirty: true })
    }
  }

  const removeCompany = (company: string) => removeFromListField("companies", company)
  const addCompany = () => addCommaSeparatedToListField("companyInput", "companies")
  const removeExcludedCompany = (company: string) => removeFromListField("excludedCompanies", company)
  const addExcludedCompany = () => addCommaSeparatedToListField("excludedCompanyInput", "excludedCompanies")

  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      reset(initialFormValues)
      setSearchFilters("")
      setActiveSection("general")
    }
    updateOpenState(newOpen)
  }

  const handleFormSubmit = async (data: FilterFormData) => {
    const filters = extractSearchFiltersDraft(data)

    try {
      setIsSubmitting(true)
      await onSubmit?.(filters)
      updateOpenState(false)
    } catch (error) {
      console.error("Failed to submit edit filters dialog", error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderEmptySection = () => (
    <div className="py-8 text-center text-muted-foreground">
      <Filter className="mx-auto mb-2 h-8 w-8" />
      <p>Select a filter category from the sidebar to configure options.</p>
    </div>
  )

  const sectionContent: Record<EditFiltersSectionId, React.ReactNode> = {
    general: <GeneralFiltersSection control={control} />,
    locations: (
      <LocationFiltersSection
        control={control}
        formValues={formValues}
        onRemoveLocation={removeLocation}
        onRemoveRegion={removeRegion}
        onRemoveStructuredLocation={removeStructuredLocation}
        onSelectLocation={selectLocation}
        setValue={setValue}
      />
    ),
    job: (
      <JobFiltersSection
        control={control}
        formValues={formValues}
        isPending={isFindingSimilarJobTitles}
        onAddJobTitle={addJobTitle}
        onFindSimilarJobTitles={handleFindSimilarJobTitles}
        onRemoveJobTitle={removeJobTitle}
        setValue={setValue}
      />
    ),
    company: (
      <CompanyFiltersSection
        control={control}
        formValues={formValues}
        onAddCompany={addCompany}
        onAddExcludedCompany={addExcludedCompany}
        onRemoveCompany={removeCompany}
        onRemoveExcludedCompany={removeExcludedCompany}
        setValue={setValue}
      />
    ),
    skills: (
      <SkillsFiltersSection
        canFindSimilarKeywords={formValues.jobTitles.length > 0}
        control={control}
        formValues={formValues}
        isFindingSimilarKeywords={isFindingSimilarKeywords}
        isFindingSimilarSkills={isFindingSimilarSkills}
        onAddKeyword={addKeyword}
        onFindSimilarKeywords={handleFindSimilarKeywords}
        onAddSkill={addSkill}
        onFindSimilarSkills={handleFindSimilarSkills}
        onRemoveKeyword={removeKeyword}
        onRemoveSkill={removeSkill}
        setValue={setValue}
      />
    ),
    funding: renderEmptySection(),
    power: renderEmptySection(),
    likely: renderEmptySection(),
    education: renderEmptySection(),
    languages: renderEmptySection(),
    boolean: renderEmptySection(),
  }

  return (
    <EditFiltersDialogShell
      activeSection={activeSection}
      children={children}
      content={sectionContent[activeSection] ?? renderEmptySection()}
      description={description}
      hideInactiveFilters={formValues.hideInactiveFilters}
      isControlled={isControlled}
      isOpen={isOpen}
      isSubmitting={isSubmitting}
      onActiveSectionChange={setActiveSection}
      onHideInactiveFiltersChange={(checked) => setValue("hideInactiveFilters", checked, { shouldDirty: true })}
      onOpenChange={handleOpenChange}
      onSearchFiltersChange={setSearchFilters}
      onSubmit={handleSubmit(handleFormSubmit)}
      searchFilters={searchFilters}
      submitLabel={submitLabel}
      title={title}
      visibleSections={visibleSections}
    />
  )
}
